import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { adminAPI, departmentsAPI } from '../api/client';
import {
  ROLES, ROLE_LABELS, ROLE_BADGE_COLOR, useAuth,
  MODULES, MODULE_ACTIONS,
} from '../context/AuthContext';
import Modal from '../components/Common/Modal';
import {
  PlusIcon, TrashIcon, PencilIcon, ShieldCheckIcon, KeyIcon,
  UsersIcon, ClipboardDocumentListIcon, ArrowPathIcon, Cog6ToothIcon,
  MagnifyingGlassIcon, ChevronUpIcon, ChevronDownIcon, ServerStackIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import IntegrationsTab from '../components/Admin/integrations/IntegrationsTab';
import UserActivityPanel from '../components/Admin/UserActivityPanel';
import SystemHealthTab from '../components/Admin/SystemHealthTab';

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

const fmtLastSeen = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  if (diffMin < 1440 * 7) return `${Math.floor(diffMin / 1440)}d ago`;
  return d.toLocaleDateString();
};

// Two-tier model: Admin (super_admin) or Staff (dept_staff).
const TIER_OPTIONS = [
  { value: ROLES.SUPER_ADMIN, label: 'Admin', desc: 'Full access to everything. No restrictions.' },
  { value: ROLES.DEPT_STAFF,  label: 'Staff', desc: 'Access controlled by the module permission grid below.' },
];

const blankCell = () => ({ view: false, add: false, edit: false, delete: false });

const blankForm = () => ({
  email: '',
  full_name: '',
  password: '',
  role: ROLES.DEPT_STAFF,
  department_id: '',
  module_permissions: {}, // keyed by module key → {view,add,edit,delete}
});

export default function AdminPanel() {
  const [tab, setTab] = useState(
    () => (new URLSearchParams(window.location.search).get('tab') === 'integrations' ? 'integrations' : 'users')
  );

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      <header className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldCheckIcon className="w-5 h-5 text-primary-600" /> Admin Panel
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage users, permissions, and audit activity.</p>
      </header>

      <div className="flex gap-1 mb-5 border-b border-slate-200">
        <TabButton
          icon={UsersIcon}
          label="Users"
          active={tab === 'users'}
          onClick={() => setTab('users')}
        />
        <TabButton
          icon={Cog6ToothIcon}
          label="Integrations"
          active={tab === 'integrations'}
          onClick={() => setTab('integrations')}
        />
        <TabButton
          icon={ClipboardDocumentListIcon}
          label="Audit Log"
          active={tab === 'audit'}
          onClick={() => setTab('audit')}
        />
        <TabButton
          icon={ServerStackIcon}
          label="System Health"
          active={tab === 'health'}
          onClick={() => setTab('health')}
        />
      </div>

      {tab === 'users' ? <UsersTab />
        : tab === 'integrations' ? <IntegrationsTab />
        : tab === 'health' ? <SystemHealthTab />
        : <AuditTab />}
    </div>
  );
}

function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// USERS TAB
// ═══════════════════════════════════════════════════════════════════

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');     // name | role | last_seen | status
  const [sortDir, setSortDir] = useState('asc');      // asc | desc
  const [activityUser, setActivityUser] = useState(null);
  const [deactivating, setDeactivating] = useState(null);  // user pending deactivation confirm
  const [togglingId, setTogglingId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([adminAPI.getUsers(), departmentsAPI.list()]);
      setUsers(u.data);
      setDepartments(d.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditUser(null);
    setForm(blankForm());
    setShowFormModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    // Anything that isn't Admin (super_admin) is treated as Staff in the two-tier model.
    const tier = u.role === ROLES.SUPER_ADMIN ? ROLES.SUPER_ADMIN : ROLES.DEPT_STAFF;
    setForm({
      email: u.email,
      full_name: u.full_name,
      password: '',
      role: tier,
      department_id: u.department_id || '',
      module_permissions: u.module_permissions || {},
    });
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isAdminTier = form.role === ROLES.SUPER_ADMIN;

    const payload = {
      full_name: form.full_name,
      role: form.role,
      department_id: form.department_id ? parseInt(form.department_id) : null,
    };
    // Staff → send the module grid; Admin → backend grants full access by role.
    if (!isAdminTier) {
      payload.module_permissions = form.module_permissions || {};
    }
    if (!editUser) {
      payload.email = form.email;
      payload.password = form.password;
    } else if (form.password) {
      payload.password = form.password;
    }

    try {
      if (editUser) {
        const res = await adminAPI.updateUser(editUser.id, payload);
        setUsers((prev) => prev.map((u) => (u.id === editUser.id ? res.data : u)));
        toast.success('User updated');
      } else {
        const res = await adminAPI.createUser(payload);
        setUsers((prev) => [res.data, ...prev]);
        toast.success('User created');
      }
      setShowFormModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.full_name}"?`)) return;
    try {
      await adminAPI.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── Active/inactive ──────────────────────────────────────────────────
  const setActive = async (u, active) => {
    setTogglingId(u.id);
    try {
      const res = await adminAPI.updateUser(u.id, { is_active: active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data : x)));
      toast.success(active ? 'User activated' : 'User deactivated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setTogglingId(null);
      setDeactivating(null);
    }
  };

  const onToggleActive = (u) => {
    if (u.is_active) setDeactivating(u);   // confirm before deactivating
    else setActive(u, true);               // activate immediately
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ── Stats + filtered/sorted list ─────────────────────────────────────
  const stats = useMemo(() => {
    const active = users.filter((u) => u.is_active).length;
    const onlineToday = users.filter((u) => isToday(u.last_seen)).length;
    return { total: users.length, active, inactive: users.length - active, onlineToday };
  }, [users]);

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? users.filter((u) =>
          (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      : [...users];

    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'role':      av = a.role || ''; bv = b.role || ''; break;
        case 'last_seen': av = a.last_seen ? new Date(a.last_seen).getTime() : 0;
                          bv = b.last_seen ? new Date(b.last_seen).getTime() : 0; break;
        case 'status':    av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
        default:          av = (a.full_name || '').toLowerCase(); bv = (b.full_name || '').toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [users, search, sortKey, sortDir]);

  // ── Module-grid quick-fill helpers (two-tier model) ──────────────────
  const fullCell = () => ({ view: true, add: true, edit: true, delete: true });
  const readCell = () => ({ view: true, add: false, edit: false, delete: false });

  const applyModuleAll = (mode) => {
    setForm((f) => {
      if (mode === 'clear') return { ...f, module_permissions: {} };
      const g = {};
      MODULES.forEach((m) => { g[m.key] = mode === 'full' ? fullCell() : readCell(); });
      return { ...f, module_permissions: g };
    });
  };

  const applyModuleRow = (moduleKey, mode) => {
    setForm((f) => ({
      ...f,
      module_permissions: {
        ...(f.module_permissions || {}),
        [moduleKey]: mode === 'full' ? fullCell() : readCell(),
      },
    }));
  };

  const setModuleCell = (moduleKey, action, value) => {
    setForm((f) => {
      const g = { ...(f.module_permissions || {}) };
      const cell = { ...blankCell(), ...(g[moduleKey] || {}) };
      cell[action] = value;
      // A write permission implies view; clearing view clears the whole row.
      if (action !== 'view' && value) cell.view = true;
      if (action === 'view' && !value) { cell.add = cell.edit = cell.delete = false; }
      g[moduleKey] = cell;
      return { ...f, module_permissions: g };
    });
  };

  return (
    <>
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatTile label="Total users" value={stats.total} className="bg-slate-50 text-slate-700" />
        <StatTile label="Active" value={stats.active} className="bg-green-50 text-green-700" />
        <StatTile label="Inactive" value={stats.inactive} className="bg-slate-100 text-slate-500" />
        <StatTile label="Online today" value={stats.onlineToday} className="bg-primary-50 text-primary-700" />
      </div>

      {/* Toolbar: search + create */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm flex-shrink-0"
        >
          <PlusIcon className="w-4 h-4" /> New User
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <SortTh label="Name" colKey="name" sortKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortTh label="Role" colKey="role" sortKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Departments</th>
              <SortTh label="Last seen" colKey="last_seen" sortKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
              <SortTh label="Status" colKey="status" sortKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                </td>
              </tr>
            )}
            {!loading && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                  {search ? 'No users match your search' : 'No users yet'}
                </td>
              </tr>
            )}
            {visibleUsers.map((u) => {
              const isSelf = me?.id === u.id;
              return (
                <tr
                  key={u.id}
                  onClick={() => setActivityUser(u)}
                  className="border-b border-slate-50 hover:bg-slate-50/50 last:border-0 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">
                      {u.full_name}
                      {isSelf && (
                        <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">
                          YOU
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ROLE_BADGE_COLOR[u.role] || 'bg-slate-100 text-slate-700'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 max-w-[240px]">
                    <DeptPermissionPills permissions={u.permissions} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtLastSeen(u.last_seen)}</td>
                  <td className="px-4 py-3 text-slate-500">{u.actions_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                      <button
                        onClick={(e) => { e.stopPropagation(); if (!isSelf) onToggleActive(u); }}
                        disabled={isSelf || togglingId === u.id}
                        title={isSelf ? "You can't change your own status" : (u.is_active ? 'Deactivate user' : 'Activate user')}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          u.is_active ? 'bg-green-500' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          u.is_active ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <IconButton
                        title="Reset password"
                        onClick={(e) => { e.stopPropagation(); setShowResetModal(u); }}
                        icon={KeyIcon}
                        hoverClass="hover:text-amber-600 hover:bg-amber-50"
                      />
                      <IconButton
                        title="Edit user"
                        onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                        icon={PencilIcon}
                        hoverClass="hover:text-primary-600 hover:bg-primary-50"
                      />
                      {!isSelf && (
                        <IconButton
                          title="Delete user"
                          onClick={(e) => { e.stopPropagation(); deleteUser(u); }}
                          icon={TrashIcon}
                          hoverClass="hover:text-red-600 hover:bg-red-50"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit user modal */}
      <Modal
        open={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={editUser ? `Edit User: ${editUser.full_name}` : 'Create New User'}
        size="lg"
      >
        <UserForm
          form={form}
          setForm={setForm}
          editing={!!editUser}
          departments={departments}
          onSubmit={handleSubmit}
          onCancel={() => setShowFormModal(false)}
          applyModuleAll={applyModuleAll}
          applyModuleRow={applyModuleRow}
          setModuleCell={setModuleCell}
        />
      </Modal>

      {/* Reset password modal */}
      <ResetPasswordModal
        user={showResetModal}
        onClose={() => setShowResetModal(null)}
      />

      {/* Deactivation confirm */}
      <Modal
        open={!!deactivating}
        onClose={() => togglingId ? null : setDeactivating(null)}
        title="Deactivate user?"
        size="sm"
      >
        <p className="text-sm text-slate-600 mb-4">
          <span className="font-semibold text-slate-900">{deactivating?.full_name}</span> will no longer
          be able to sign in until reactivated. Existing sessions remain valid until their token expires.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setDeactivating(null)}
            disabled={!!togglingId}
            className="px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => setActive(deactivating, false)}
            disabled={!!togglingId}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50"
          >
            {togglingId ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </Modal>

      {/* Per-user activity slide-in */}
      {activityUser && (
        <UserActivityPanel user={activityUser} onClose={() => setActivityUser(null)} />
      )}
    </>
  );
}

function StatTile({ label, value, className }) {
  return (
    <div className={`rounded-xl px-4 py-3 ${className}`}>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="mt-1 text-xs font-medium opacity-80">{label}</p>
    </div>
  );
}

function SortTh({ label, colKey, sortKey, dir, onSort }) {
  const active = sortKey === colKey;
  return (
    <th className="text-left px-4 py-3 font-semibold text-slate-600">
      <button onClick={() => onSort(colKey)} className="inline-flex items-center gap-1 hover:text-slate-900">
        {label}
        {active && (dir === 'asc' ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />)}
      </button>
    </th>
  );
}

function IconButton({ title, onClick, icon: Icon, hoverClass }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`p-1.5 text-slate-400 rounded-lg transition-colors ${hoverClass}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

function DeptPermissionPills({ permissions }) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-slate-400 italic">No departments</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {permissions.map((p) => {
        const bits = [];
        if (p.can_create) bits.push('C');
        if (p.can_read) bits.push('R');
        if (p.can_update) bits.push('U');
        if (p.can_delete) bits.push('D');
        return (
          <span
            key={p.department_id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium"
          >
            {p.department_name}
            <span className="text-[10px] font-mono text-slate-500">{bits.join('')}</span>
          </span>
        );
      })}
    </div>
  );
}

// ─── User form ─────────────────────────────────────────────────────

function UserForm({ form, setForm, editing, departments, onSubmit, onCancel, applyModuleAll, applyModuleRow, setModuleCell }) {
  const isAdmin = form.role === ROLES.SUPER_ADMIN;
  const isStaff = !isAdmin;

  const onRoleChange = (role) => setForm({ ...form, role });

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Full Name *</label>
          <input
            required
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Email *</label>
          <input
            required type="email"
            value={form.email}
            disabled={editing}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-slate-50 disabled:text-slate-400"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
          Password {editing ? '(leave blank to keep current)' : '*'}
        </label>
        <input
          type="password"
          value={form.password}
          required={!editing}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder={editing ? 'Leave blank to keep current' : '••••••••'}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Access Tier</label>
        <div className="grid grid-cols-2 gap-2">
          {TIER_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${
                form.role === opt.value
                  ? 'border-primary-400 bg-primary-50/50'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={opt.value}
                checked={form.role === opt.value}
                onChange={(e) => onRoleChange(e.target.value)}
                className="mt-0.5 accent-primary-600"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
          Primary Department <span className="font-normal text-slate-400 normal-case">(auto-expanded in sidebar)</span>
        </label>
        <select
          value={form.department_id}
          onChange={(e) => setForm({ ...form, department_id: e.target.value })}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">— None —</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      {isAdmin && (
        <InfoBox color="purple">
          Admins have unrestricted access to every module. No permission grid needed.
        </InfoBox>
      )}

      {isStaff && (
        <ModuleGrid
          grid={form.module_permissions || {}}
          onSetCell={setModuleCell}
          onRow={applyModuleRow}
          onAll={applyModuleAll}
        />
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
        <button type="submit"
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 shadow-sm">
          {editing ? 'Save Changes' : 'Create User'}
        </button>
      </div>
    </form>
  );
}

function InfoBox({ color = 'primary', children }) {
  const colors = {
    purple: 'bg-purple-50 border-purple-100 text-purple-800',
    amber: 'bg-amber-50 border-amber-100 text-amber-800',
    primary: 'bg-primary-50 border-primary-100 text-primary-800',
  };
  return (
    <div className={`px-4 py-3 rounded-xl border text-xs ${colors[color]}`}>
      {children}
    </div>
  );
}

function ModuleGrid({ grid, onSetCell, onRow, onAll }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Module Permissions
        </label>
        <div className="flex gap-1.5 text-[11px]">
          <button type="button" onClick={() => onAll('read')}
            className="px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
            Read Only All
          </button>
          <button type="button" onClick={() => onAll('full')}
            className="px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
            Full Access All
          </button>
          <button type="button" onClick={() => onAll('clear')}
            className="px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
            Clear All
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase tracking-wide text-slate-500">
              <th className="text-left px-3 py-2 font-semibold">Module</th>
              <th className="text-center px-2 py-2 font-semibold w-14">View</th>
              <th className="text-center px-2 py-2 font-semibold w-14">Add</th>
              <th className="text-center px-2 py-2 font-semibold w-14">Edit</th>
              <th className="text-center px-2 py-2 font-semibold w-14">Delete</th>
              <th className="text-center px-2 py-2 font-semibold w-28">Quick</th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map((m) => {
              const cell = grid[m.key] || {};
              return (
                <tr key={m.key} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">{m.label}</span>
                  </td>
                  {MODULE_ACTIONS.map((action) => (
                    <td key={action} className="text-center px-2 py-2">
                      <input
                        type="checkbox"
                        checked={Boolean(cell[action])}
                        onChange={(e) => onSetCell(m.key, action, e.target.checked)}
                        className="accent-primary-600"
                      />
                    </td>
                  ))}
                  <td className="text-center px-2 py-2">
                    <div className="flex gap-1 justify-center">
                      <button type="button" onClick={() => onRow(m.key, 'read')}
                        className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-[10px] text-slate-600 font-medium">
                        Read Only
                      </button>
                      <button type="button" onClick={() => onRow(m.key, 'full')}
                        className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-[10px] text-slate-600 font-medium">
                        Full
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        Staff can only perform the checked actions. Selecting Add/Edit/Delete automatically enables View.
      </p>
    </div>
  );
}

// ─── Reset password modal ──────────────────────────────────────────

function ResetPasswordModal({ user, onClose }) {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { setNewPassword(''); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    try {
      await adminAPI.resetPassword(user.id, newPassword);
      toast.success(`Password reset for ${user.full_name}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={!!user} onClose={onClose} title="Reset password" size="sm">
      <p className="text-sm text-slate-600 mb-4">
        Set a new password for <span className="font-semibold text-slate-900">{user?.full_name}</span>.
        They will need to use this password on their next sign-in.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          type="password"
          required
          minLength={4}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="New password"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={loading}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-xl hover:bg-amber-700 shadow-sm disabled:opacity-50">
            {loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AUDIT LOG TAB
// ═══════════════════════════════════════════════════════════════════

const ACTION_BADGE = {
  user_created:        { color: 'bg-green-100 text-green-700',   label: 'User Created' },
  user_updated:        { color: 'bg-blue-100 text-blue-700',     label: 'User Updated' },
  user_deleted:        { color: 'bg-red-100 text-red-700',       label: 'User Deleted' },
  user_password_reset: { color: 'bg-amber-100 text-amber-700',   label: 'Password Reset' },
  login:               { color: 'bg-green-100 text-green-700',   label: 'Login' },
  logout:              { color: 'bg-slate-100 text-slate-600',   label: 'Logout' },
  document_upload:     { color: 'bg-blue-100 text-blue-700',     label: 'Document Upload' },
  ai_chat:             { color: 'bg-violet-100 text-violet-700', label: 'AI Chat' },
  department_created:  { color: 'bg-green-100 text-green-700',   label: 'Department Created' },
  department_updated:  { color: 'bg-blue-100 text-blue-700',     label: 'Department Updated' },
  department_deleted:  { color: 'bg-red-100 text-red-700',       label: 'Department Deleted' },
  project_created:     { color: 'bg-green-100 text-green-700',   label: 'Project Created' },
  project_updated:     { color: 'bg-blue-100 text-blue-700',     label: 'Project Updated' },
  project_deleted:     { color: 'bg-red-100 text-red-700',       label: 'Project Deleted' },
  project_archived:    { color: 'bg-amber-100 text-amber-700',   label: 'Project Archived' },
  project_restored:    { color: 'bg-green-100 text-green-700',   label: 'Project Restored' },
};

/**
 * Who or what caused an audit row.
 *
 * THIS REPLACED THE WORD "deleted", which was a false statement. The old cell
 * read `log.user_name ? name : "deleted"` — keyed on the NAME being absent,
 * never on any evidence a user had been removed. Measured on production
 * 2026-09-07: 97 of 639 rows rendered "deleted", every one of them
 * actor_kind='candidate', while the number of users ever deleted was zero.
 * A null user_id means "no user acted", not "the user is gone", and the two
 * are indistinguishable from user_id alone — which is exactly why the
 * actor_kind column exists (see backend/services/actor_context.py).
 *
 * An UNRECOGNISED actor_kind renders its raw value rather than falling back.
 * The taxonomy has five classes and only three appear in production so far, so
 * the first scheduler or webhook row must be VISIBLE as a new thing, not
 * silently absorbed into a default that makes it look handled.
 */
function AuditActor({ log }) {
  const kind = log.actor_kind;

  // A real person. Also the path for the 450 rows that predate the send audit:
  // actor_kind is null there, but every one carries a real user_id, so they
  // render exactly as before.
  if (kind === 'user' || !kind) {
    if (log.user_name) {
      return (
        <>
          <p className="text-sm font-medium text-slate-700">{log.user_name}</p>
          <p className="text-[11px] text-slate-400">{log.user_email}</p>
        </>
      );
    }
    // actor_kind says a user acted but the name is missing. This is the ONLY
    // case where a user record really could be gone, and it is stated as an
    // open question rather than as the fact the old cell asserted.
    return (
      <span className="text-xs text-slate-500">
        User {log.user_id ? `#${log.user_id}` : '(unknown id)'}
      </span>
    );
  }

  if (kind === 'candidate') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
        Candidate{log.actor_candidate_id ? ` #${log.actor_candidate_id}` : ''}
      </span>
    );
  }

  if (kind === 'scheduler' || kind === 'system') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
        {kind === 'scheduler' ? 'Scheduler' : 'System'}
      </span>
    );
  }

  // UNKNOWN MUST LOOK WRONG. It does not mean "some other actor" — it means no
  // entry point bound one, which actor_context.py calls a defect rather than a
  // category, and which audit_send already logs at WARNING with the call site.
  // Styling it like the others would hide a bug behind a tidy label.
  if (kind === 'unknown') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-300 px-2 py-0.5 rounded-full"
            title="No entry point bound an actor for this row. This is a defect, not a kind of actor.">
        ⚠ Unknown
      </span>
    );
  }

  // A class this UI has never heard of. Show it verbatim.
  return <span className="text-xs font-medium text-amber-700">{kind}</span>;
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAction) params.action = filterAction;
      const res = await adminAPI.getAuditLogs(params);
      setLogs(res.data);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction]);

  useEffect(() => { load(); }, [load]);

  const uniqueActions = useMemo(() => {
    const set = new Set(logs.map((l) => l.action));
    return Array.from(set).sort();
  }, [logs]);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <p className="text-sm text-slate-500">{logs.length} entr{logs.length === 1 ? 'y' : 'ies'}</p>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            <option value="">All actions</option>
            {Object.keys(ACTION_BADGE).map((a) => (
              <option key={a} value={a}>{ACTION_BADGE[a].label}</option>
            ))}
            {uniqueActions.filter((a) => !ACTION_BADGE[a]).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Time</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">User</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Action</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Resource</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Department</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                </td>
              </tr>
            )}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400">No activity recorded yet</td>
              </tr>
            )}
            {logs.map((log) => {
              const badge = ACTION_BADGE[log.action];
              return (
                <tr key={log.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <AuditActor log={log} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${badge ? badge.color : 'bg-slate-100 text-slate-700'}`}>
                      {badge ? badge.label : log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.resource_type
                      ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {log.department_name || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 max-w-[260px]">
                    <DetailsCell raw={log.details} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * `raw` is now an OBJECT, not a JSON string.
 *
 * audit_logs.details became JSONB on 2026-09-07, so the API deserialises it
 * and hands this component a plain object. The old body called
 * JSON.parse(raw) unconditionally; against an object that throws
 * ("[object Object]" is not JSON), the catch swallowed it, and the cell
 * rendered the literal text [object Object] for every audited row. It would
 * not have crashed, which is exactly why it needed changing rather than
 * waiting to be noticed.
 *
 * The string branch is kept, not for compatibility with a rolled-back
 * backend, but because it costs one line and the failure it prevents is a
 * silent one.
 */
/* ── The four payload shapes services/audit_edit.py can produce ──────────────
 *
 * Detected by shape, not by field name, because the field name is the audited
 * column and could be anything. Order matters below: the sha pair is checked
 * before the from/to pair, and a truncated descriptor can appear INSIDE a
 * from/to pair as either side, so it is handled by the value renderer rather
 * than at the top level.
 */
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
//: {from, to} — an ordinary before/after change
const isFromTo = (v) => isObj(v) && ('from' in v || 'to' in v);
//: {from_sha256, to_sha256} — email template body, hashed by design
const isShaPair = (v) => isObj(v) && ('from_sha256' in v || 'to_sha256' in v);
//: {changed: true} — candidate email; the value was deliberately NOT stored
const isChangedOnly = (v) => isObj(v) && v.changed === true && !('from' in v);
//: {truncated, length, sha256} — a free-text value over the 4KB cap
const isTruncated = (v) => isObj(v) && v.truncated === true;

/** One side of a change, as short readable text. Never an object. */
function auditValue(v) {
  if (isTruncated(v)) return `too long to store (${v.length} chars)`;
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return v === '' ? '""' : v;
  if (Array.isArray(v)) return `[${v.length}]`;
  if (isObj(v)) return JSON.stringify(v);
  return String(v);
}

/**
 * The `details` column of one audit row.
 *
 * THIRD REVISION. Read the previous two before touching it again:
 *   1. actor_kind — unrelated, but it moved the User column out of this file.
 *   2. JSONB — `raw` stopped being a JSON string and became an object. The
 *      old body called JSON.parse() on it unconditionally; the throw was
 *      swallowed by the existing catch and the cell printed the literal text
 *      [object Object]. Degraded silently rather than crashing.
 *   3. THIS ONE — services/audit_edit.py now writes STRUCTURED values, so a
 *      value is often {from, to} rather than a scalar. String({from:1,to:2})
 *      is "[object Object]", so the rows this whole audit exists to produce
 *      would have been the ones rendering as garbage. Known in advance this
 *      time rather than discovered later.
 *
 * WHAT MUST NOT REGRESS: the 450 rows that predate the send audit and the 16
 * hand-audited actions all carry PLAIN SCALAR details ({"name": "HR"}). Those
 * take the last branch below and render with byte-identical markup to before.
 *
 * OVERFLOW: the preview still shows three fields, because the column is
 * capped at 260px and a from/to chip is roughly twice the width of a scalar
 * one. `+N more` is now a BUTTON that expands the rest in place, rather than
 * a dead label — a position update touching six fields was otherwise
 * unreadable, which is exactly the case this feature produces. Expansion is
 * local state and resets if the tab refreshes; that is a transient view
 * toggle, not work being lost.
 */
function DetailsCell({ raw }) {
  const [expanded, setExpanded] = useState(false);

  if (!raw) return <span className="text-slate-300">—</span>;
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { return <span className="truncate inline-block max-w-full">{raw}</span>; }
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return <span className="truncate inline-block max-w-full">{String(parsed)}</span>;
  }
  const keys = Object.keys(parsed);
  if (keys.length === 0) return <span className="text-slate-300">—</span>;

  const shown = expanded ? keys : keys.slice(0, 3);

  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((k) => {
        const v = parsed[k];

        // Hashed — the template body. Reads as "changed"; the hashes are on
        // hover only, because nobody compares sha256 by eye and putting them
        // in the cell would push everything else out of 260px.
        if (isShaPair(v)) {
          return (
            <span key={k}
                  title={`before: ${v.from_sha256 || '(none)'}\nafter:  ${v.to_sha256 || '(none)'}`}
                  className="inline-flex gap-1 items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">
              <span className="text-slate-500">{k}:</span>
              <span className="text-slate-700">changed</span>
            </span>
          );
        }

        // Value deliberately not recorded — candidate email. Amber and
        // spelled out, so it cannot be mistaken either for a value or for an
        // ordinary from/to change.
        if (isChangedOnly(v)) {
          return (
            <span key={k}
                  title="This field changed. Its value is deliberately not recorded — see services/audit_edit.py"
                  className="inline-flex gap-1 items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] font-mono">
              <span className="text-amber-700">{k}:</span>
              <span className="text-amber-800">changed (value not recorded)</span>
            </span>
          );
        }

        // The ordinary case: old → new, both visible.
        if (isFromTo(v)) {
          const from = auditValue(v.from);
          const to = auditValue(v.to);
          return (
            <span key={k}
                  title={`${k}\nbefore: ${from}\nafter:  ${to}`}
                  className="inline-flex gap-1 items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">
              <span className="text-slate-500">{k}:</span>
              <span className="text-rose-700 truncate max-w-[90px]">{from}</span>
              <span className="text-slate-400">→</span>
              <span className="text-emerald-700 truncate max-w-[90px]">{to}</span>
            </span>
          );
        }

        // A bare truncated descriptor, not inside a from/to pair.
        if (isTruncated(v)) {
          return (
            <span key={k} title={`sha256: ${v.sha256}`}
                  className="inline-flex gap-1 items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">
              <span className="text-slate-500">{k}:</span>
              <span className="text-slate-700">{auditValue(v)}</span>
            </span>
          );
        }

        // UNCHANGED from before this revision: plain scalars, which is every
        // pre-existing row. Same markup, same classes, same truncation.
        return (
          <span key={k} className="inline-flex gap-1 items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono">
            <span className="text-slate-500">{k}:</span>
            <span className="text-slate-700 truncate max-w-[80px]">{String(parsed[k])}</span>
          </span>
        );
      })}
      {keys.length > 3 && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="text-[10px] text-slate-400 hover:text-slate-600 underline decoration-dotted"
        >
          {expanded ? 'show less' : `+${keys.length - 3} more`}
        </button>
      )}
    </div>
  );
}

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
